import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";
import { ToolCard } from "@/components/calv/ToolCard";
import { jsonLdScript, breadcrumbSchema, pageHead } from "@/lib/seo";
import { SITE_URL } from "@/lib/locale";

export const Route = createFileRoute("/en/services/tools")({
  head: () => {
    const h = pageHead("tools", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
            jsonLdScript(breadcrumbSchema([
              { name: "Home", path: "/en" },
              { name: "Services", path: "/en/services" },
              { name: "Tools", path: "/en/services/tools" },
            ])),
            jsonLdScript({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: "Tools — Calvarius",
              url: `${SITE_URL}/en/services/tools`,
              hasPart: [
                { "@type": "SoftwareApplication", name: "Delta", applicationCategory: "BusinessApplication", url: `${SITE_URL}/en/services/delta` },
                { "@type": "SoftwareApplication", name: "picazzoo", applicationCategory: "BusinessApplication", url: `${SITE_URL}/en/services/picazzoo` },
                { "@type": "SoftwareApplication", name: "GonzalAds", applicationCategory: "BusinessApplication", url: `${SITE_URL}/en/services/gonzalads` },
              ],
            }),
          ],
    };
  },
  component: ToolsHubEN,
});

function ToolsHubEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow="Tools"
          headline={<>We build the tools <em>we work with.</em></>}
          lead="Performance mandates don't scale through more headcount – they scale through better tools. Over recent years, Calvarius has built three proprietary tools that create speed, precision, and profitability exactly where standard software hits its limits – feed management, creative production, and Meta campaign steering. Plus the capability to build custom software for mandates in a fraction of the previous time."
          metaStrip={["Three proprietary tools", "AI-driven development", "Built for mandates"]}
          breadcrumbs={[{ label: "Home", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "Tools" }]}
        />

        <Section number="02" kicker="Standpoint" headline={<>No standard tooling. <em>Proprietary software.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              <p>Most agencies use standard software for all mandates. That's efficient for the agency but rarely optimal for the client – license costs scale with volume, workflows adapt only partially to mandate-specific needs, and every platform change means waiting for updates from third-party roadmaps.</p>
              <p>We take a different path. Where standard tools systematically become too slow, too expensive, or too rigid, we build our own software. Three tools are productively in use today: <strong className="font-semibold text-ink">Delta</strong> for feed management, <strong className="font-semibold text-ink">picazzoo</strong> for AI-driven creative production, <strong className="font-semibold text-ink">GonzalAds</strong> for Meta campaign steering.</p>
              <p>These tools are not an end in themselves. They emerge from concrete bottlenecks in mandates – and continue to evolve as new bottlenecks appear. We apply the same principle to custom software projects for our mandates: AI-driven development tools now enable custom solutions in weeks where months were previously needed.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Three tools" headline={<>In productive use. <em>Not in pitch decks.</em></>}>
          <div className="grid gap-4 md:grid-cols-3">
            <ToolCard
              brand="Delta"
              tagline="Feed management. Scaled. Versioned. Without license fees."
              bullets={["Clean pipelines per platform", "Platform-specific optimization instead of one-size feeds", "Versioned, auditable"]}
              href="/en/services/delta"
              cta="Learn more"
            />
            <ToolCard
              brand="picazzoo"
              tagline="AI creatives. Banners, videos, product galleries — automated."
              bullets={["Ads and videos for all performance platforms", "Product gallery images using product data", "Brand-consistent across channels"]}
              href="/en/services/picazzoo"
              cta="Learn more"
            />
            <ToolCard
              brand="GonzalAds"
              tagline="Meta campaign control. Faster, more precise, deeper than native."
              bullets={["AI wizard for campaigns, ad sets, ads", "Rule-based automation of optimization and scaling", "Performance analytics far beyond native Meta reports"]}
              href="/en/services/gonzalads"
              cta="Learn more"
            />
          </div>
        </Section>

        <Section number="04" kicker="Custom SaaS development" headline={<>When none of the three tools fits, <em>we build one.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              <p>Some problems can't be solved with standard software – they need custom tools. Data pipelines, internal dashboards, configurators, small SaaS components, interface integrations.</p>
              <p>What's changed: AI-driven development tools now enable custom software projects in a fraction of the time previously needed. This makes custom software economically viable for an order of magnitude more mandates that previously had to settle for standard tools.</p>
              <p>We don't build every request, but only where custom software has real economic leverage. If you need a tool that doesn't exist yet, let's talk.</p>
            </div>
          </RevealOnScroll>
          <div className="mt-10">
            <CTABlock
              headline={<>Discuss <em>your idea?</em></>}
              body="Sketch out what's missing — we'll check whether custom software is actually the right lever here."
              primary={{ label: "Discuss your idea", to: "/en/contact" }}
              secondary={{ label: "Digital service development", to: "/en/services/digital-development" }}
            />
          </div>
        </Section>

        <Section kicker="Note" headline={<>Public <em>analysis tools</em></>}>
          <div className="max-w-2xl">
            <p className="text-[17px] leading-relaxed text-ink-2">
              Calvarius also operates public analysis tools for self-diagnosis — Lighthouse check and profitability calculator. These are available under{" "}
              <Link to="/en/tools" className="text-cyan-deep underline decoration-cyan/40 underline-offset-4 hover:decoration-cyan-deep">
                Calvarius tools
              </Link>.
            </p>
          </div>
        </Section>

        <LogoReferences locale="en" number="05" />

        <RelatedServices
          locale="en"
          fromId="tools"
          number="06"
          items={relatedBySource["tools"]}
        />

        <Section variant="deep" number="07" kicker="Demo">
          <CTABlock
            variant="deep"
            headline={<>Want to see how our tools <em>work in a mandate?</em></>}
            body="A 30-minute demo shows more than any slide. We'll show you DELTA, picazzoo, or GonzalAds with a real example — either live or based on a comparable mandate setup."
            primary={{ label: "Request a demo", to: "/en/contact" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
